import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, Feather } from "lucide-react";
import { NAV_LINKS } from "@/lib/site";
import { scrollToId } from "@/lib/lenis";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setOpen(false);
    setTimeout(() => scrollToId(id), open ? 250 : 0);
  };

  return (
    <header
      className={`fixed top-0 z-50 w-full transition-colors duration-500 ${
        scrolled ? "border-b border-gold/20 bg-lake-deep/85 backdrop-blur-xl" : "bg-transparent"
      }`}
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-4 sm:px-6 lg:px-8">
        <button
          data-testid="nav-author-logo"
          onClick={() => go("top")}
          className="flex items-center gap-2 text-left"
        >
          <Feather className="h-5 w-5 text-gold" strokeWidth={1.5} />
          <span className="font-serif text-lg leading-none text-parchment-ivory">
            Il Segreto <span className="text-gold-shimmer">del Lago</span>
            <span className="ml-2 align-middle font-sans text-[9px] uppercase tracking-[0.3em] text-gold/70">
              La Saga
            </span>
          </span>
        </button>

        <nav className="hidden items-center gap-7 lg:flex" aria-label="Navigazione principale">
          {NAV_LINKS.map((l) => (
            <button
              key={l.id}
              data-testid={`nav-link-${l.id}`}
              onClick={() => go(l.id)}
              className="text-[13px] font-medium tracking-wide text-parchment-ivory/75 transition-colors duration-300 hover:text-gold-shimmer"
            >
              {l.label}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <button
            data-testid="nav-preorder-button"
            onClick={() => go("libro")}
            className="hidden rounded-full bg-gold px-5 py-2 text-[13px] font-semibold text-ink-dark transition-transform duration-300 hover:-translate-y-0.5 sm:inline-flex"
          >
            Acquista Ora
          </button>
          <button
            data-testid="nav-menu-toggle"
            onClick={() => setOpen((v) => !v)}
            className="inline-flex rounded-full border border-gold/30 p-2 text-parchment-ivory lg:hidden"
            aria-label="Apri menu"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.35 }}
            className="overflow-hidden border-b border-gold/20 bg-lake-deep/95 backdrop-blur-xl lg:hidden"
            aria-label="Menu mobile"
          >
            <div className="flex flex-col gap-1 px-6 py-4">
              {NAV_LINKS.map((l, i) => (
                <motion.button
                  key={l.id}
                  initial={{ opacity: 0, x: -12 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.05 * i }}
                  onClick={() => go(l.id)}
                  className="py-2.5 text-left font-serif text-lg text-parchment-ivory/85"
                >
                  {l.label}
                </motion.button>
              ))}
              <button
                onClick={() => go("libro")}
                className="mt-3 rounded-full bg-gold px-5 py-3 text-sm font-semibold text-ink-dark"
              >
                Acquista Ora
              </button>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}
